import React from "react";
import { NickLogo, PayoffShine } from "@/components/BrandLogo";
import { Heart, GraduationCap, Users, BookOpen, Lightbulb, School, Monitor } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

const PROGRAMS = [
  {
    icon: Monitor,
    title: "Google My Business per ragazzi",
    desc: "I ragazzi imparano a creare e gestire una scheda Google per l'attivita' dei genitori o del quartiere.",
    target: "Medie e Superiori",
    color: "#4285F4",
  },
  {
    icon: BookOpen,
    title: "Fotografia 360° e Street View",
    desc: "Laboratorio pratico con GoPro MAX: i ragazzi fotografano la scuola e la pubblicano su Google Maps.",
    target: "Elementari e Medie",
    color: "#EA4335",
  },
  {
    icon: Lightbulb,
    title: "Reputazione digitale",
    desc: "Come funzionano le recensioni, perche' contano, come rispondere. Educazione al digitale consapevole.",
    target: "Medie e Superiori",
    color: "#FBBC04",
  },
  {
    icon: Users,
    title: "Il mio quartiere su Google Maps",
    desc: "Progetto di classe: mappare le attivita' del quartiere, intervistare i commercianti, creare contenuti.",
    target: "Elementari",
    color: "#34A853",
  },
];

const COLLAB = [
  { name: "Scuole elementari", desc: "Laboratori creativi di mappatura digitale del territorio" },
  { name: "Scuole medie", desc: "Percorsi di educazione digitale e fotografia 360°" },
  { name: "Scuole superiori", desc: "Alternanza scuola-lavoro e progetti di marketing territoriale" },
  { name: "Associazioni locali", desc: "Partnership per eventi di formazione digitale sul territorio" },
];

export default function AcademyPage() {
  return (
    <div className="min-h-screen bg-[#0a0e17]">
      {/* Hero — S.com Cares */}
      <section className="relative py-20 overflow-hidden" data-testid="academy-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e17] via-[#0c1220] to-[#0a0e17]"></div>
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <div className="inline-block mb-6">
            <NickLogo size={120} showPayoff={false} />
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black italic text-white mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <span style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Cares</span>
          </h1>

          <p className="text-gray-400 text-sm mb-4">
            Il programma di responsabilita' sociale di <span className="text-white font-bold">schedabusiness.com</span>
          </p>

          <div className="inline-block mb-8">
            <span className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
              <span className="text-[#EA4335]">#</span>its4kids
            </span>
          </div>

          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed mb-8">
            Collaboriamo con <span className="font-bold text-white">associazioni e scuole</span> per formare sul digitale le nuove generazioni.
            Elementari, medie e superiori.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            {["Elementari", "Medie", "Superiori"].map((level, i) => (
              <div key={i} className="relative rounded-full p-[1.5px] overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-60 rounded-full"></div>
                <div className="relative bg-[#0a0e17] rounded-full px-5 py-2">
                  <span className="text-white font-bold text-sm flex items-center gap-2">
                    <School className="w-4 h-4 text-[#FBBC04]" />
                    {level}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-[#060a12]" data-testid="academy-mission">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="bg-[#EA4335]/15 text-[#EA4335] border-[#EA4335]/30 mb-3 rounded-full px-4 py-1">
              <Heart className="w-4 h-4 mr-2" />
              La nostra missione
            </Badge>
            <h2 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Come <span className="text-[#EA4335]">NBA Cares</span> sta alla NBA,{" "}
              <span className="inline-flex items-baseline gap-1">
                <NickLogo size={28} showPayoff={false} />
              </span>{" "}
              sta a schedabusiness.com
            </h2>
            <p className="text-gray-400 text-sm mt-3 max-w-2xl mx-auto">
              Il digitale non e' solo business. E' uno strumento che puo' cambiare la vita delle comunita' locali.
              Per questo portiamo la formazione digitale nelle scuole — gratuitamente.
            </p>
          </div>

          <div className="relative rounded-2xl p-[2px] overflow-hidden mb-10">
            <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-40"></div>
            <div className="relative bg-[#0c1220] rounded-[14px] p-8 text-center">
              <blockquote className="text-xl md:text-2xl font-black italic text-white mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                "Il futuro digitale dei nostri ragazzi inizia oggi, nella loro scuola."
              </blockquote>
              <p className="text-gray-400 text-sm">
                — Riccardo Rossi, fondatore schedabusiness.com
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Programmi */}
      <section className="py-16 bg-[#0a0e17]" data-testid="academy-programs">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="bg-[#4285F4]/15 text-[#4285F4] border-[#4285F4]/30 mb-3 rounded-full px-4 py-1">
              <GraduationCap className="w-4 h-4 mr-2" />
              Programmi formativi
            </Badge>
            <h2 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Cosa insegniamo <span className="text-2xl md:text-3xl font-black text-[#EA4335]">#its4kids</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {PROGRAMS.map((prog, i) => (
              <div
                key={i}
                className="group relative bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 hover:bg-white/[0.05] transition-colors"
                data-testid={`academy-program-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${prog.color}15`, border: `1px solid ${prog.color}30` }}>
                    <prog.icon className="w-6 h-6" style={{ color: prog.color }} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-lg mb-1">{prog.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-3">{prog.desc}</p>
                    <Badge className="rounded-full text-[10px] px-3 py-0.5" style={{ background: `${prog.color}15`, color: prog.color, border: `1px solid ${prog.color}30` }}>
                      <School className="w-3 h-3 mr-1" />
                      {prog.target}
                    </Badge>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Collaborazioni */}
      <section className="py-16 bg-[#060a12]" data-testid="academy-collab">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="bg-[#34A853]/15 text-[#34A853] border-[#34A853]/30 mb-3 rounded-full px-4 py-1">
              <Users className="w-4 h-4 mr-2" />
              Collaborazioni
            </Badge>
            <h2 className="text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Con chi lavoriamo
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {COLLAB.map((c, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5">
                <h3 className="text-white font-bold text-base mb-1 flex items-center gap-2">
                  <School className="w-4 h-4 text-[#34A853]" />
                  {c.name}
                </h3>
                <p className="text-gray-400 text-sm">{c.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-gray-400 text-sm mb-4">
              Sei un dirigente scolastico o un'associazione? Parliamone.
            </p>
            <a
              href="https://wa.me/393773745184?text=Ciao%20Riccardo!%20Sono%20interessato%20al%20programma%20S.com%20Cares%20%23its4kids%20per%20la%20nostra%20scuola/associazione."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-full px-8 py-3 text-sm transition-colors"
              data-testid="academy-contact-cta"
            >
              Contattaci su WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16 bg-[#0a0e17]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="relative rounded-2xl p-[2px] overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC04] to-[#34A853] opacity-50"></div>
            <div className="relative bg-[#0a0e17] rounded-[14px] p-10">
              <NickLogo size={56} showPayoff={false} />
              <h2 className="text-3xl font-black italic text-white mt-4 mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                <span style={{ background: 'linear-gradient(135deg, #D4AF37, #F5E6A3, #D4AF37, #B8942E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Cares</span>
              </h2>
              <p className="text-2xl font-black text-white mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>
                <span className="text-[#EA4335]">#</span>its4kids
              </p>
              <p className="text-gray-400 text-sm">
                Elementari · Medie · Superiori
              </p>
              <p className="text-gray-500 text-xs mt-4">
                Un progetto di <span className="text-white font-bold">schedabusiness.com</span> per le nuove generazioni
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
